<p-dialog
  header="Charts Dashboard"
  [modal]="true"
  [draggable]="false"
  [style]="{ width: 'min(92rem, 98vw)' }"
  [breakpoints]="{ '1024px': '98vw', '640px': '100vw' }"
  [(visible)]="visible"
  (onShow)="onDialogShow()"
  (onHide)="onDialogHide()"
>
  @if (!loadSettled()) {
    <div class="py-16 text-center text-surface-switch-600">Loading charts…</div>
  } @else {
    @if (charts(); as charts) {
      @let currencyCode = displayCurrencyCode();
      <div class="flex flex-col gap-6 pr-1 text-surface-switch-900">
        <div class="flex justify-end">
          <p-button
            label="Export to CSV"
            icon="pi pi-download"
            [disabled]="!loadSettled()"
            (onClick)="exportToCsv()"
          />
        </div>
        <section class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <div
            class="rounded-xl border border-surface-switch-200 bg-surface-switch-0 p-5 shadow-sm"
          >
            <div
              class="text-xs font-medium uppercase tracking-wide text-surface-switch-500"
            >
              Total Time Required
            </div>
            <div class="mt-2 text-3xl font-bold tabular-nums">
              {{ charts.summary.total_time_required_hours | number: "1.2-4" }}
            </div>
            <div class="mt-1 text-sm text-surface-switch-500">
              hours (program)
            </div>
          </div>

          <div
            class="rounded-xl border border-surface-switch-200 bg-surface-switch-0 p-5 shadow-sm"
          >
            <div
              class="text-xs font-medium uppercase tracking-wide text-surface-switch-500"
            >
              Total Cost
            </div>
            <div class="mt-2 text-3xl font-bold tabular-nums">
              {{ charts.summary.total_cost_required | currency: currencyCode }}
            </div>
            <div class="mt-1 text-sm text-surface-switch-500">root parts</div>
          </div>

          <div
            class="rounded-xl border border-surface-switch-200 bg-surface-switch-0 p-5 shadow-sm"
          >
            <div
              class="text-xs font-medium uppercase tracking-wide text-surface-switch-500"
            >
              Time per Assembly
            </div>
            <div class="mt-2 text-3xl font-bold tabular-nums">
              {{ charts.summary.time_per_assembly | number: "1.2-4" }}
            </div>
            <div class="mt-1 text-sm text-surface-switch-500">
              hours / assembly
            </div>
          </div>

          <div
            class="rounded-xl border border-surface-switch-200 bg-surface-switch-0 p-5 shadow-sm"
          >
            <div
              class="text-xs font-medium uppercase tracking-wide text-surface-switch-500"
            >
              Monthly Labor Load
            </div>
            <div class="mt-2 text-3xl font-bold tabular-nums">
              {{ charts.summary.monthly_required_hours | number: "1.2-4" }}
            </div>
            <div class="mt-1 text-sm text-surface-switch-500">
              hr / mo · {{ charts.summary.m_days_per_month }} M-days / mo
            </div>
          </div>
        </section>

        <section class="grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
          <div
            class="rounded-xl border border-surface-switch-200 bg-surface-switch-0 p-4 shadow-sm"
          >
            <div class="mb-3">
              <h3 class="m-0 text-base font-semibold text-surface-switch-800">
                Capacity vs Required Hours
              </h3>
              <p
                class="mt-1 m-0 text-xs leading-relaxed text-surface-switch-500"
              >
                1 / 2 / 3 shifts · required spread over program months ·
                capacity = usable hr × M-days / mo
              </p>
            </div>
            <div class="chart-tile chart-tile--md">
              <p-chart
                type="bar"
                [data]="capacityVsRequiredChartData()"
                [options]="capacityGroupedBarChartOptions"
              />
            </div>
          </div>

          <div
            class="rounded-xl border border-surface-switch-200 bg-surface-switch-0 p-4 shadow-sm"
          >
            <div class="mb-3 shrink-0">
              <h3 class="m-0 text-base font-semibold text-surface-switch-800">
                Completion Time by Shift
              </h3>
              <p
                class="mt-1 m-0 text-xs leading-relaxed text-surface-switch-500"
              >
                Months = program labor hours ÷ monthly capacity
              </p>
            </div>
            <div class="chart-tile chart-tile--md">
              <p-chart
                type="bar"
                [data]="completionMonthsChartData()"
                [options]="completionGroupedBarChartOptions"
              />
            </div>
          </div>

          <div
            class="flex min-h-0 flex-col rounded-xl border border-surface-switch-200 bg-surface-switch-0 p-4 shadow-sm lg:col-span-2"
          >
            <div class="mb-3 shrink-0">
              <h3 class="m-0 text-base font-semibold text-surface-switch-800">
                Cost Composition by Part
              </h3>
              <p class="mt-1 m-0 text-xs text-surface-switch-500">
                Stacked material, labour, and CapEx costs
              </p>
            </div>
            <div
              class="chart-tile w-full"
              [style.height.px]="horizontalCompositionChartHeightPx()"
            >
              <p-chart
                type="bar"
                [data]="costCompositionChartData()"
                [options]="horizontalStackedBarChartOptions()"
              />
            </div>
          </div>

          <div
            class="rounded-xl border border-surface-switch-200 bg-surface-switch-0 p-4 shadow-sm"
          >
            <div class="mb-3 shrink-0">
              <h3 class="m-0 text-base font-semibold text-surface-switch-800">
                Time per Part
              </h3>
              <p class="mt-1 m-0 text-xs text-surface-switch-500">
                Hours by root line
              </p>
            </div>
            <div
              class="chart-tile w-full"
              [style.height.px]="horizontalPartChartHeightPx()"
            >
              <p-chart
                type="bar"
                [data]="timePerPartChartData()"
                [options]="timePerPartBarChartOptions()"
              />
            </div>
          </div>

          <div
            class="rounded-xl border border-surface-switch-200 bg-surface-switch-0 p-4 shadow-sm"
          >
            <div class="mb-3 shrink-0">
              <h3 class="m-0 text-base font-semibold text-surface-switch-800">
                Cost per Part
              </h3>
              <p class="mt-1 m-0 text-xs text-surface-switch-500">
                Subtotal cost by root line
              </p>
            </div>
            <div
              class="chart-tile w-full"
              [style.height.px]="horizontalPartChartHeightPx()"
            >
              <p-chart
                type="bar"
                [data]="costPerPartChartData()"
                [options]="costPerPartBarChartOptions()"
              />
            </div>
          </div>

          <div
            class="flex min-h-0 flex-col rounded-xl border border-surface-switch-200 bg-surface-switch-0 p-4 shadow-sm lg:col-span-2"
          >
            <div
              class="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
            >
              <div>
                <h3 class="m-0 text-base font-semibold text-surface-switch-800">
                  Process Breakdown by Part
                </h3>
                <p class="mt-1 m-0 text-xs text-surface-switch-500">
                  Select a root part to show time and cost for its child line
                  items
                </p>
              </div>
              <p-select
                [ngModel]="selectedPartPersistentId()"
                (ngModelChange)="selectedPartPersistentId.set($event)"
                [options]="charts.parts"
                optionLabel="name"
                optionValue="persistent_id"
                placeholder="Select part"
                class="w-full sm:w-80"
              />
            </div>

            @if (selectedProcessRows().length) {
              <div class="grid grid-cols-1 gap-5 lg:grid-cols-2">
                <div>
                  <div class="mb-3 shrink-0">
                    <h4
                      class="m-0 text-sm font-semibold text-surface-switch-800"
                    >
                      Time per Process
                    </h4>
                    <p class="mt-1 m-0 text-xs text-surface-switch-500">
                      Hours by child line item for {{ selectedPartName() }}
                    </p>
                  </div>
                  <div
                    class="chart-tile w-full"
                    [style.height.px]="horizontalProcessChartHeightPx()"
                  >
                    <p-chart
                      type="bar"
                      [data]="timePerProcessChartData()"
                      [options]="timePerProcessBarChartOptions()"
                    />
                  </div>
                </div>

                <div>
                  <div class="mb-3 shrink-0">
                    <h4
                      class="m-0 text-sm font-semibold text-surface-switch-800"
                    >
                      Cost per Process
                    </h4>
                    <p class="mt-1 m-0 text-xs text-surface-switch-500">
                      Subtotal cost by child line item for
                      {{ selectedPartName() }}
                    </p>
                  </div>
                  <div
                    class="chart-tile w-full"
                    [style.height.px]="horizontalProcessChartHeightPx()"
                  >
                    <p-chart
                      type="bar"
                      [data]="costPerProcessChartData()"
                      [options]="costPerProcessBarChartOptions()"
                    />
                  </div>
                </div>
              </div>

              <div class="mt-5">
                <div class="mb-3 shrink-0">
                  <h4 class="m-0 text-sm font-semibold text-surface-switch-800">
                    Cost Composition by Process
                  </h4>
                  <p class="mt-1 m-0 text-xs text-surface-switch-500">
                    Stacked material, labour, and CapEx costs for
                    {{ selectedPartName() }}
                  </p>
                </div>
                <div
                  class="chart-tile w-full"
                  [style.height.px]="
                    horizontalProcessCompositionChartHeightPx()
                  "
                >
                  <p-chart
                    type="bar"
                    [data]="costCompositionByProcessChartData()"
                    [options]="horizontalStackedProcessBarChartOptions()"
                  />
                </div>
              </div>
            } @else {
              <div
                class="rounded-lg border border-dashed border-surface-switch-200 py-10 text-center text-sm text-surface-switch-500"
              >
                {{ selectedPartName() }} has no child process line items to
                chart.
              </div>
            }
          </div>

          <div
            class="rounded-xl border border-surface-switch-200 bg-surface-switch-0 p-4 shadow-sm"
          >
            <div class="mb-3 shrink-0">
              <h3 class="m-0 text-base font-semibold text-surface-switch-800">
                Time Distribution
              </h3>
              <p class="mt-1 m-0 text-xs text-surface-switch-500">
                Share of labor-time by part
              </p>
            </div>
            <div class="chart-tile chart-tile--pie">
              <p-chart
                type="pie"
                [data]="timeDistributionPieData()"
                [options]="pieChartOptions"
              />
            </div>
          </div>
        </section>

        <section class="grid grid-cols-1 gap-3 md:grid-cols-3">
          @for (row of charts.completion_time_by_shift; track row.shift_label) {
            <div
              class="rounded-xl border border-surface-switch-200 bg-surface-switch-0 p-4 shadow-sm"
            >
              <div class="text-sm font-semibold text-surface-switch-700">
                {{ row.shift_label }}
              </div>
              <div class="mt-3 text-2xl font-bold tabular-nums">
                {{ row.months | number: "1.2-4" }}
              </div>
              <div class="text-sm text-surface-switch-500">months</div>
              <div class="mt-2 text-sm tabular-nums text-surface-switch-700">
                {{ row.days | number: "1.1-1" }} days
              </div>
            </div>
          }
        </section>
      </div>
    } @else {
      <div class="py-16 text-center text-surface-switch-600">
        No chart data available.
      </div>
    }
  }

  <ng-template pTemplate="footer">
    <div class="flex justify-end pt-3">
      <p-button label="Close" severity="secondary" (onClick)="close()" />
    </div>
  </ng-template>
</p-dialog>
